import { normalizeSiblingOrder } from './normalizeSiblingOrder';

/**
 * @typedef {import('../../../../types/LiveReports2').LiveReports2Doc} LiveReports2Doc
 *
 * @typedef {object} ReportCopyOptions
 * @property {string} client - CompagnyID the copy belongs to
 * @property {boolean} isTemplate - Whether the copy is a template
 * @property {string} newId - ID of the new report
 * @property {string} name - Name of the new report
 * @property {number} position - Position of the new report in the tenant's list
 * @property {any} timestamp - Firestore timestamp for Timestamp/CreatedAt/UpdatedAt
 */

/**
 * Build the documents for a copy of a report: a fresh draft plus its widgets.
 * Nodig omdat createNewReportFromSource dit inline deed en zo niet te toetsen was.
 *
 * Only the layout comes along. Who the report is about (PersonaID), how it is
 * shared (ShareCode) and its publication status belong to the source — a copy
 * starts as a draft about the whole organisation.
 *
 * @param {LiveReports2Doc} source - The report being copied
 * @param {Array<{ID: string, Parent: string|null, Order?: number, docid: string, Deleted?: boolean}>} sourceWidgets - Serialized widget docs of the source, with their docid
 * @param {ReportCopyOptions} options
 * @returns {{report: LiveReports2Doc, widgets: Array<object>}}
 */
const buildReportCopy = (source, sourceWidgets, { client, isTemplate, newId, name, position, timestamp }) => {
    /** @type {LiveReports2Doc} */
    const report = {
        ID: newId,
        CompagnyID: client,
        IsTemplate: isTemplate,
        Name: name,
        Position: position,
        Timestamp: timestamp,
        CreatedAt: timestamp,
        UpdatedAt: timestamp,
        Status: 'draft',
        // Carry the overall period along — the copied widgets keep their
        // period settings (mode 'inherit'), so dropping Start/End would
        // silently revert a periodised report to all-time data.
        Start: source.Start || null,
        End: source.End || null,
        // A template copy is a template again and keeps its module gate
        // (isTemplateAvailable), or a participant template shows up for
        // every tenant. An own report from a template has no gate.
        // AI-CONTEXT: See docs/pitfalls.md#dev-new-gate-field-reaches-one-reader
        ...(isTemplate && Array.isArray(source.RequiredModules) && source.RequiredModules.length
            ? { RequiredModules: [...source.RequiredModules] }
            : {}),
        // Locked travels with every copy: an own report from a locked
        // template is locked, and copying a locked report must not be the
        // way out of the standard. Whether it blocks editing is
        // isReportLocked's call (a template stays editable for Deccos).
        // AI-CONTEXT: See docs/features/meetstandaardrapportage.md (T1)
        ...(source.Locked === true ? { Locked: true } : {}),
    };

    // Siblings can share an Order value; the viewer then falls back to
    // docid order. The copies get new docids, so re-number Order per
    // parent first or tied siblings render shuffled in the copy.
    // AI-CONTEXT: See docs/pitfalls.md#database-livereport-widget-order-docid-tiebreak
    const widgets = normalizeSiblingOrder(sourceWidgets.filter(w => !w.Deleted))
        .map(data => ({
            ...data,
            // Drop the source's docid — it's denormalized for the deserializer
            // and must be null on new docs so the editor creates fresh updates
            // instead of writing through to the source.
            docid: null,
            ReportId: newId,
            CompagnyID: client,
            IsTemplate: isTemplate,
        }));

    return { report, widgets };
};

/**
 * Naam van een kopie: 'Kopie van <naam>', zodat de kopie in de lijst als
 * kopie herkenbaar is (B2, /test 2026-10-20). Alleen voor kopiëren —
 * nieuw-uit-sjabloon houdt 'Live rapportage <datum>'.
 *
 * @param {{Name?: string}} source - Het rapport dat gekopieerd wordt
 * @returns {string}
 */
export const copyReportName = (source) => `Kopie van ${source?.Name?.trim() || 'naamloze rapportage'}`;

export default buildReportCopy;
